import { Box, Input, Portal, Spinner, Text, VStack } from '@chakra-ui/react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ConfirmDialog, useEscapeToClosePopup } from '@mangovoice/mango-ui'

import { useLocationsSearch } from '../hooks/useExtensionReferenceData'
import { useExtensionsI18n } from '../i18n/provider'
import type { LocationOption } from '../types/api.types'

const DEBOUNCE_MS = 300
const DROPDOWN_MAX_HEIGHT = '240px'

interface UpdateLocationDialogProps {
  isOpen: boolean
  onClose: () => void
  selectedCount: number
  pbxId: number | undefined | null
  onConfirm: (locationId: number) => void | Promise<void>
  isSubmitting: boolean
}

export const UpdateLocationDialog = React.memo<UpdateLocationDialogProps>(
  ({ isOpen, onClose, selectedCount, pbxId, onConfirm, isSubmitting }) => {
    const { text } = useExtensionsI18n()
    const [searchTerm, setSearchTerm] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [selectedLocation, setSelectedLocation] =
      useState<LocationOption | null>(null)
    const [dropdownOpen, setDropdownOpen] = useState(false)
    useEscapeToClosePopup(dropdownOpen, () => setDropdownOpen(false))
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      const timer = setTimeout(
        () => setDebouncedSearch(searchTerm),
        DEBOUNCE_MS,
      )
      return () => clearTimeout(timer)
    }, [searchTerm])

    const { locations, isLoading } = useLocationsSearch(
      pbxId ?? undefined,
      debouncedSearch,
    )

    useEffect(() => {
      if (!isOpen) {
        setSearchTerm('')
        setDebouncedSearch('')
        setSelectedLocation(null)
        setDropdownOpen(false)
      }
    }, [isOpen])

    const handleSubmit = useCallback(() => {
      if (!selectedLocation) return
      onConfirm(selectedLocation.id)
      setSelectedLocation(null)
      setSearchTerm('')
    }, [onConfirm, selectedLocation])

    const handleClose = useCallback(() => {
      if (!isSubmitting) {
        setSearchTerm('')
        setSelectedLocation(null)
        setDropdownOpen(false)
        onClose()
      }
    }, [isSubmitting, onClose])

    const handleSelectLocation = useCallback((loc: LocationOption) => {
      setSelectedLocation(loc)
      setSearchTerm('')
      setDropdownOpen(false)
    }, [])

    const canSubmit = selectedLocation != null && !isSubmitting

    const inputDisplayValue = useMemo(() => {
      if (dropdownOpen) return searchTerm
      return selectedLocation?.name ?? ''
    }, [dropdownOpen, searchTerm, selectedLocation])

    const emptyStateText = useMemo(() => {
      if (isLoading)
        return text(
          'extensions.massEdit.loadingLocations',
          'Loading locations\u2026',
        )
      if (searchTerm.trim())
        return text(
          'extensions.massEdit.noLocationsFound',
          'No locations found',
        )
      return text(
        'extensions.massEdit.typeToSearchLocations',
        'Type to search locations',
      )
    }, [isLoading, searchTerm, text])

    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
    useEffect(() => {
      if (dropdownOpen && inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect()
        setDropdownStyle({
          position: 'fixed',
          top: `${rect.bottom + 4}px`,
          left: `${rect.left}px`,
          width: `${Math.max(rect.width, 280)}px`,
        })
      }
    }, [dropdownOpen])

    return (
      <ConfirmDialog
        open={isOpen}
        onClose={handleClose}
        title={text('extensions.updateLocation.title', 'Update location')}
        description={text(
          'extensions.updateLocation.description',
          'Update location for {{count}} line{{suffix}}?',
          {
            count: selectedCount,
            suffix: selectedCount !== 1 ? 's' : '',
          },
        )}
        confirmLabel={text(
          'extensions.updateLocation.confirm',
          'Update location',
        )}
        cancelLabel={text('extensions.common.cancel', 'Cancel')}
        onConfirm={handleSubmit}
        isLoading={isSubmitting}
        confirmDisabled={!canSubmit}
        confirmColorPalette="brand"
      >
        <Box mb={4} position="relative">
          <Text fontSize="sm" fontWeight="medium" color="fg.subtle" mb={2}>
            {text('extensions.massEdit.location', 'Location')}
          </Text>
          <Input
            ref={inputRef}
            value={inputDisplayValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearchTerm(e.target.value)
              setDropdownOpen(true)
              if (selectedLocation) setSelectedLocation(null)
            }}
            onFocus={() => setDropdownOpen(true)}
            onBlur={() => setTimeout(() => setDropdownOpen(false), 200)}
            placeholder={text(
              'extensions.massEdit.typeToSearchLocations',
              'Type to search locations',
            )}
            disabled={isSubmitting || pbxId == null}
            bg="bg.subtle"
            borderColor="border.default"
            aria-label={text(
              'extensions.massEdit.locationSearchLabel',
              'Location search',
            )}
            aria-autocomplete="list"
            aria-expanded={dropdownOpen}
          />
          {isLoading && (
            <Box
              position="absolute"
              right={3}
              top="50%"
              transform="translateY(-50%)"
              mt={1}
              pointerEvents="none"
            >
              <Spinner size="sm" />
            </Box>
          )}
          {dropdownOpen && pbxId != null && (
            <Portal>
              <Box
                style={dropdownStyle}
                zIndex={1500}
                bg="bg.panel"
                borderWidth="1px"
                borderColor="border.default"
                borderRadius="md"
                shadow="lg"
                maxHeight={DROPDOWN_MAX_HEIGHT}
                overflowY="auto"
              >
                {locations.length === 0 ? (
                  <Box p={4} textAlign="center" color="fg.muted">
                    <Text fontSize="sm">{emptyStateText}</Text>
                  </Box>
                ) : (
                  <VStack gap={0} align="stretch" py={1}>
                    {locations.map((loc) => (
                      <Box
                        key={loc.id}
                        px={4}
                        py={2}
                        cursor="pointer"
                        bg={
                          selectedLocation?.id === loc.id
                            ? 'blue.50'
                            : 'transparent'
                        }
                        _hover={{ bg: 'gray.50' }}
                        onClick={() => handleSelectLocation(loc)}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <Text fontWeight="medium">{loc.name}</Text>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>
            </Portal>
          )}
        </Box>
      </ConfirmDialog>
    )
  },
)

UpdateLocationDialog.displayName = 'UpdateLocationDialog'
